// JuryDeliberationPage - Jury de Deliberation page
// Displays the annual deliberation PV with full grade table, decision management,
// PV generation, and export capabilities.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Scale, Loader2, AlertCircle, CheckCircle, Download, FileText,
  Save, RefreshCw, Printer, ChevronDown, XCircle, Award, Lock, GraduationCap,
} from 'lucide-react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Permission, UserRole } from '@/types/global.types';
import {
  getJuryFilters, getPvByFilters, generatePv, updateDecisions,
  regenerateGrades, exportPvPdf, exportPvExcel, exportDiplomesExcel, signPv,
  applyProgression, applyProgressionForYear,
} from '@/services/juryDeliberationService';
import type {
  JuryFiltersResponse, JuryPvData, JuryStudentRow, JuryPvSemester,
  StudentDecisionUpdate,
} from '@/types/jury-deliberation.types';
import { PRE_JURY_DECISIONS, OFFICIEL_DECISIONS, MENTIONS } from '@/types/jury-deliberation.types';
import { formatAcademicYearName } from '@/utils/academicYear';

// ==================== HELPERS ====================

function gradeColor(grade: number | null): string {
  if (grade === null) return '';
  if (grade < 10) return 'text-red-600 bg-red-50';
  if (grade < 12) return 'text-amber-600 bg-amber-50';
  return 'text-green-600 bg-green-50';
}

function computeMention(annualAverage: number | null): string {
  if (annualAverage === null) return '';
  if (annualAverage >= 16) return 'Très Bien';
  if (annualAverage >= 14) return 'Bien';
  if (annualAverage >= 12) return 'Assez Bien';
  if (annualAverage >= 10) return 'Passable';
  return '';
}

function mapRoleToFrench(role: string | null): string {
  const roleMap: Record<string, string> = {
    SUPER_ADMIN: 'Super Administrateur',
    DIRECTOR_GENERAL: 'Directeur General',
    BRANCH_MANAGER: 'Directeur de Campus',
    PEDAGOGICAL_DIRECTOR: 'Directeur Pedagogique',
    FINANCIAL_MANAGER: 'Directeur Financier',
    MARKETING_MANAGER: 'Directeur Marketing',
    COMMERCIAL_MANAGER: 'Directeur Commercial',
    CALL_CENTER_AGENT: 'Agent Centre d\'Appel',
    PROGRAM_HEAD: 'Chef de Filiere',
    ACADEMIC_COORDINATOR: 'Coordinateur Academique',
    TEACHER: 'Professeur',
    STUDENT: 'Etudiant',
    PARENT: 'Parent',
  };
  return role ? (roleMap[role] || role) : '';
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Count total matiere-level columns (element columns) within a semester */
function countSemesterElementCols(semester: JuryPvSemester): number {
  let count = 0;
  for (const mod of semester.modules) {
    count += mod.elements.length; // one col per element
    count += 1; // module average col
  }
  count += 1; // semester average col
  return count;
}

/** Count total element + module avg columns within a semester (for Row 1 colSpan) */
function countSemesterSpan(semester: JuryPvSemester): number {
  return countSemesterElementCols(semester);
}

interface LocalDecision {
  decisionPreJury: string;
  decisionOfficiel: string;
  mention: string;
}

// ==================== SEMESTER COLORS ====================

const SEMESTER_COLORS: Record<number, { header: string; moduleHeader: string; avgBg: string; semAvgBg: string }> = {
  1: {
    header: 'bg-blue-500 text-white',
    moduleHeader: 'bg-blue-100 text-blue-800',
    avgBg: 'bg-blue-100 text-blue-800 font-semibold',
    semAvgBg: 'bg-blue-200 text-blue-900 font-bold',
  },
  2: {
    header: 'bg-emerald-500 text-white',
    moduleHeader: 'bg-emerald-100 text-emerald-800',
    avgBg: 'bg-emerald-100 text-emerald-800 font-semibold',
    semAvgBg: 'bg-emerald-200 text-emerald-900 font-bold',
  },
};

function getSemesterColor(num: number) {
  return SEMESTER_COLORS[num] || SEMESTER_COLORS[1];
}

// ==================== MAIN COMPONENT ====================

const JuryDeliberationPage: React.FC = () => {
  // Permission check for manage actions
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const canManage = hasPermission(Permission.JURY_MANAGE);

  // ---- Filter data ----
  const [filtersData, setFiltersData] = useState<JuryFiltersResponse | null>(null);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState<string | null>(null);

  // ---- Selected filters ----
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');
  const [selectedFiliereId, setSelectedFiliereId] = useState<string>('');
  const [selectedOptionId, setSelectedOptionId] = useState<string>('');
  const [selectedNiveau, setSelectedNiveau] = useState<number | ''>('');
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');

  // ---- PV data ----
  const [pvData, setPvData] = useState<JuryPvData | null>(null);
  const [applyingProgression, setApplyingProgression] = useState(false);

  /**
   * A PV signed by the Directeur Pédagogique is final: only the Directeur
   * Pédagogique (or a Super Admin, for support) can still correct it.
   */
  const isPvLocked = Boolean(pvData?.isLocked);
  const canUnlockPv =
    user?.role === UserRole.PEDAGOGICAL_DIRECTOR || user?.role === UserRole.SUPER_ADMIN;
  /**
   * Deliberation decisions are signed acts: the API only accepts them from the
   * Directeur Pédagogique, the Directeur Général or a Super Admin. Other roles
   * (coordinateur, responsable de programme…) read the PV. Mirrored here so the
   * table never lets someone fill in decisions the server would reject.
   */
  const canDecide =
    user?.role === UserRole.PEDAGOGICAL_DIRECTOR ||
    user?.role === UserRole.DIRECTOR_GENERAL ||
    user?.role === UserRole.SUPER_ADMIN;
  const canEditDecisions = canManage && canDecide && (!isPvLocked || canUnlockPv);
  const [pvLoading, setPvLoading] = useState(false);
  const [pvError, setPvError] = useState<string | null>(null);

  // ---- Generate PV ----
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // ---- Decisions ----
  const [localDecisions, setLocalDecisions] = useState<Map<string, LocalDecision>>(new Map());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingDecisions, setSavingDecisions] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ---- Regenerate grades ----
  const [regenerating, setRegenerating] = useState(false);

  // ---- Sign PV ----
  const [signing, setSigning] = useState(false);

  // ---- Export dropdown ----
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ---- Toast / alerts ----
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ---- Selected filiere (parent) and its options ----
  const selectedFiliere = useMemo(
    () => filtersData?.filieres.find((f) => f.id === selectedFiliereId) ?? null,
    [selectedFiliereId, filtersData],
  );

  // ---- Available niveaux based on selected filiere ----
  const availableNiveaux = useMemo(() => {
    if (!selectedFiliere) return [];
    return Array.from({ length: selectedFiliere.nombreAnnees }, (_, i) => i + 1);
  }, [selectedFiliere]);

  // Options only exist from their specialisation start semester onward
  // (niveau N covers semesters 2N-1 and 2N). Before that niveau the cohort is
  // pure tronc commun (no Option dropdown); from that niveau on the cohort is
  // split into options, so an option MUST be chosen — tronc commun no longer
  // exists at those niveaux.
  const visibleOptions = useMemo(() => {
    if (!selectedFiliere || !selectedNiveau) return [];
    return selectedFiliere.options.filter(
      (o) =>
        o.specialisationStartSemester == null ||
        Number(selectedNiveau) >= Math.ceil(o.specialisationStartSemester / 2),
    );
  }, [selectedFiliere, selectedNiveau]);

  const optionRequired = visibleOptions.length > 0;

  // Keep the selection coherent with the niveau: drop an option that is no
  // longer available, and auto-select when only one option exists.
  useEffect(() => {
    if (selectedOptionId && !visibleOptions.some((o) => o.id === selectedOptionId)) {
      setSelectedOptionId('');
    } else if (optionRequired && visibleOptions.length === 1 && selectedOptionId !== visibleOptions[0].id) {
      setSelectedOptionId(visibleOptions[0].id);
    }
  }, [visibleOptions, optionRequired, selectedOptionId]);

  // The PV is keyed on the option's filiere id when an option is selected
  // (groupes of option years belong to the option filiere, not the parent)
  const effectiveFiliereId = selectedOptionId || selectedFiliereId;

  // ---- Whether all required filters are set ----
  // At an option niveau the option itself is mandatory (no tronc commun there)
  const filtersComplete = !!(
    selectedAcademicYearId &&
    effectiveFiliereId &&
    selectedNiveau &&
    selectedCampusId &&
    (!optionRequired || selectedOptionId)
  );

  // ==================== TOAST HELPER ====================

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  }, []);

  // ==================== LOAD FILTERS ====================

  useEffect(() => {
    const loadFilters = async () => {
      setFiltersLoading(true);
      setFiltersError(null);
      try {
        const data = await getJuryFilters();
        setFiltersData(data);

        const currentYear = data.academicYears.find((ay) => ay.isCurrent);
        if (currentYear) {
          setSelectedAcademicYearId(currentYear.id);
        } else if (data.academicYears.length > 0) {
          setSelectedAcademicYearId(data.academicYears[0].id);
        }

        // Auto-select campus if only one
        if (data.campuses.length === 1) {
          setSelectedCampusId(data.campuses[0].id);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erreur lors du chargement des filtres';
        setFiltersError(message);
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilters();
  }, []);

  // ==================== LOAD PV WHEN FILTERS CHANGE ====================

  const loadPv = useCallback(async () => {
    if (!selectedAcademicYearId || !effectiveFiliereId || !selectedNiveau || !selectedCampusId) {
      setPvData(null);
      return;
    }

    setPvLoading(true);
    setPvError(null);
    setGenerateError(null);
    try {
      const data = await getPvByFilters(
        selectedAcademicYearId,
        effectiveFiliereId,
        Number(selectedNiveau),
        selectedCampusId,
      );
      setPvData(data);

      // Initialize local decisions from server data
      if (data) {
        initLocalDecisions(data.students);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement du PV';
      setPvError(message);
    } finally {
      setPvLoading(false);
    }
  }, [selectedAcademicYearId, effectiveFiliereId, selectedNiveau, selectedCampusId]);

  useEffect(() => {
    loadPv();
  }, [loadPv]);

  // ==================== DECISION MANAGEMENT ====================

  const initLocalDecisions = useCallback((students: JuryStudentRow[]) => {
    const map = new Map<string, LocalDecision>();
    for (const s of students) {
      // Auto-calculate mention from annual average if not already set
      const mention = s.mention || computeMention(s.annualAverage);
      map.set(s.studentId, {
        decisionPreJury: s.decisionPreJury || '',
        decisionOfficiel: s.decisionOfficiel || '',
        mention,
      });
    }
    setLocalDecisions(map);
    setHasUnsavedChanges(false);
  }, []);

  const handleDecisionChange = useCallback(
    (studentId: string, field: keyof LocalDecision, value: string) => {
      setLocalDecisions((prev) => {
        const next = new Map(prev);
        const current = next.get(studentId);
        if (current) {
          next.set(studentId, { ...current, [field]: value });
        }
        return next;
      });
      setHasUnsavedChanges(true);
      setSaveSuccess(false);
    },
    [],
  );

  // ==================== SAVE DECISIONS ====================

  const handleSaveDecisions = useCallback(async () => {
    if (!pvData) return;

    setSavingDecisions(true);
    setSaveSuccess(false);
    try {
      const decisions: StudentDecisionUpdate[] = [];
      localDecisions.forEach((dec, studentId) => {
        decisions.push({
          studentId,
          decisionPreJury: dec.decisionPreJury || undefined,
          decisionOfficiel: dec.decisionOfficiel || undefined,
          mention: dec.mention || undefined,
        });
      });

      const updated = await updateDecisions({
        juryDeliberationId: pvData.id,
        decisions,
      });

      setPvData(updated);
      initLocalDecisions(updated.students);
      setSaveSuccess(true);
      showToast('success', 'Decisions sauvegardees avec succes');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
      showToast('error', message);
    } finally {
      setSavingDecisions(false);
    }
  }, [pvData, localDecisions, initLocalDecisions, showToast]);

  // ==================== GENERATE PV ====================

  const handleGeneratePv = useCallback(async () => {
    if (!selectedAcademicYearId || !effectiveFiliereId || !selectedNiveau || !selectedCampusId) return;

    setGenerating(true);
    setGenerateError(null);
    try {
      const data = await generatePv({
        academicYearId: selectedAcademicYearId,
        filiereId: effectiveFiliereId,
        campusId: selectedCampusId,
        niveau: Number(selectedNiveau),
      });
      setPvData(data);
      initLocalDecisions(data.students);
      showToast('success', 'PV genere avec succes');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la generation du PV';
      setGenerateError(message);
    } finally {
      setGenerating(false);
    }
  }, [selectedAcademicYearId, effectiveFiliereId, selectedNiveau, selectedCampusId, initLocalDecisions, showToast]);

  // ==================== REGENERATE GRADES ====================

  const handleRegenerateGrades = useCallback(async () => {
    if (!pvData) return;

    setRegenerating(true);
    try {
      const updated = await regenerateGrades(pvData.id);
      setPvData(updated);
      initLocalDecisions(updated.students);
      showToast('success', 'Notes regenerees avec succes');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la regeneration';
      showToast('error', message);
    } finally {
      setRegenerating(false);
    }
  }, [pvData, initLocalDecisions, showToast]);

  // ==================== APPLY THE YEAR TRANSITION ====================

  /**
   * Re-applies the decisions already recorded on this PV. Needed for PVs
   * deliberated before the promotion existed — saving them again is not an
   * option once the Directeur Pedagogique has signed.
   */
  const handleApplyProgression = useCallback(async () => {
    if (!pvData) return;
    if (
      !window.confirm(
        'Appliquer le passage d\'annee pour ce PV ?\n\n' +
          'Les decisions ne sont pas modifiees. Les etudiants admis passent au niveau ' +
          'superieur et a l\'annee suivante, les admis de derniere annee deviennent diplomes, ' +
          'les redoublants restent au meme niveau, et les exclus/abandons sont archives.',
      )
    ) {
      return;
    }

    setApplyingProgression(true);
    try {
      const summary = await applyProgression(pvData.id);
      showToast(
        'success',
        `Passage d'annee applique : ${summary.promoted} promu(s), ${summary.graduated} diplome(s), ` +
          `${summary.repeated} redoublant(s), ${summary.archived} archive(s)` +
          (summary.withoutDecision > 0
            ? ` — ${summary.withoutDecision} sans decision officielle`
            : ''),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du passage d\'annee';
      showToast('error', message);
    } finally {
      setApplyingProgression(false);
    }
  }, [pvData, showToast]);

  /**
   * Same, for every PV of the selected year — the end-of-year run, and the way
   * to catch up all the PVs deliberated before this feature existed.
   */
  const handleApplyProgressionForYear = useCallback(async () => {
    if (!selectedAcademicYearId) return;
    const yearName =
      filtersData?.academicYears.find((y) => y.id === selectedAcademicYearId)?.name ?? '';
    if (
      !window.confirm(
        `Appliquer le passage d'annee a TOUS les PV de ${yearName} ?\n\n` +
          'Aucune decision n\'est modifiee. Chaque PV est traite comme individuellement : ' +
          'promotion, diplome, redoublement, archivage. L\'operation peut etre relancee sans risque.',
      )
    ) {
      return;
    }

    setApplyingProgression(true);
    try {
      const summary = await applyProgressionForYear(selectedAcademicYearId);
      showToast(
        'success',
        `${summary.pvCount} PV traite(s) : ${summary.promoted} promu(s), ${summary.graduated} diplome(s), ` +
          `${summary.repeated} redoublant(s), ${summary.archived} archive(s)` +
          (summary.withoutDecision > 0 ? ` — ${summary.withoutDecision} sans decision` : '') +
          (summary.failed > 0 ? ` — ${summary.failed} PV en erreur` : ''),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du passage d\'annee';
      showToast('error', message);
    } finally {
      setApplyingProgression(false);
    }
  }, [selectedAcademicYearId, filtersData, showToast]);

  // ==================== SIGN PV ====================

  const handleSignPv = useCallback(async () => {
    if (!pvData) return;
    setSigning(true);
    try {
      const updated = await signPv(pvData.id);
      setPvData(updated);
      showToast('success', 'PV signe avec succes');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la signature';
      showToast('error', message);
    } finally {
      setSigning(false);
    }
  }, [pvData, showToast]);

  // ==================== EXPORTS ====================

  const handleExportPdf = useCallback(async () => {
    if (!pvData) return;
    setExporting(true);
    setExportDropdownOpen(false);
    try {
      await exportPvPdf(pvData.id);
      showToast('success', 'PDF exporte avec succes');
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur lors de l'export PDF";
      showToast('error', message);
    } finally {
      setExporting(false);
    }
  }, [pvData, showToast]);

  const handleExportExcel = useCallback(async () => {
    if (!pvData) return;
    setExporting(true);
    setExportDropdownOpen(false);
    try {
      await exportPvExcel(pvData.id);
      showToast('success', 'Excel exporte avec succes');
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur lors de l'export Excel";
      showToast('error', message);
    } finally {
      setExporting(false);
    }
  }, [pvData, showToast]);

  const handleExportDiplomes = useCallback(async () => {
    if (!selectedAcademicYearId) return;
    setExporting(true);
    setExportDropdownOpen(false);
    try {
      await exportDiplomesExcel(selectedAcademicYearId);
      showToast('success', 'Liste des diplomes exportee');
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur lors de l'export des diplomes";
      showToast('error', message);
    } finally {
      setExporting(false);
    }
  }, [selectedAcademicYearId, showToast]);

  // ==================== COLUMN STRUCTURE (memoized) ====================

  const columnStructure = useMemo(() => {
    if (!pvData) return null;

    const semesters = pvData.semesters;
    let totalGradeCols = 0;

    const semesterMeta = semesters.map((sem) => {
      const modules = sem.modules.map((mod) => {
        const elementCount = mod.elements.length;
        totalGradeCols += elementCount + 1; // elements + module avg
        return {
          ...mod,
          colSpan: elementCount + 1, // element cols + module avg col
        };
      });
      totalGradeCols += 1; // semester average col
      return {
        ...sem,
        modules,
        totalColSpan: countSemesterSpan(sem),
      };
    });

    // Fixed cols: NOM, PRENOM = 2
    // Decision cols: Pre-jury, Officiel, Mention = 3
    const decisionCols = 3;
    // Annual average = 1
    const totalCols = 2 + totalGradeCols + 1 + decisionCols;

    return { semesterMeta, totalGradeCols, decisionCols, totalCols };
  }, [pvData]);

  // ==================== SORTED STUDENTS ====================

  const sortedStudents = useMemo(() => {
    if (!pvData) return [];
    return [...pvData.students].sort((a, b) =>
      a.studentName.localeCompare(b.studentName, 'fr'),
    );
  }, [pvData]);

  // ==================== RESET ON FILIERE CHANGE ====================

  useEffect(() => {
    // When filiere changes, reset niveau if it's no longer in the available list
    if (selectedNiveau !== '' && !availableNiveaux.includes(Number(selectedNiveau))) {
      setSelectedNiveau('');
    }
  }, [selectedFiliereId, availableNiveaux, selectedNiveau]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setExportDropdownOpen(false);
    if (exportDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    return undefined;
  }, [exportDropdownOpen]);

  // ==================== LOADING STATE (FILTERS) ====================

  if (filtersLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (filtersError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Erreur de chargement</h3>
          <p className="mt-2 text-sm text-gray-500">{filtersError}</p>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6">
      {/* Print CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: landscape; margin: 5mm; }
          table { font-size: 6pt !important; }
          .print-only { display: block !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Toast notification */}
      {toastMessage && (
        <div
          className={`no-print fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg transition-all ${
            toastMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {toastMessage.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{toastMessage.text}</span>
        </div>
      )}

      {/* ==================== HEADER ==================== */}
      <div className="no-print flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <Scale className="w-8 h-8 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Jury de Deliberation</h1>
            <p className="text-gray-500">Proces-verbal annuel des deliberations</p>
          </div>
        </div>

        {/* Action buttons - visible only when PV exists and user has manage permission */}
        {pvData && canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Unsaved changes indicator */}
            {hasUnsavedChanges && (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 font-medium">
                Modifications non sauvegardees
              </span>
            )}

            {/* Save button */}
            <button
              onClick={handleSaveDecisions}
              disabled={savingDecisions || !hasUnsavedChanges || !canEditDecisions}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                hasUnsavedChanges
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {savingDecisions ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : saveSuccess ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Sauvegarder
            </button>

            {/* Regenerate grades */}
            <button
              onClick={handleRegenerateGrades}
              disabled={regenerating || !canEditDecisions}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-all"
            >
              {regenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Regenerer les notes
            </button>

            {/* Passage d'annee: re-applies decisions already recorded (PVs
                deliberated before this feature, or signed and thus read-only) */}
            {canDecide && (
              <button
                onClick={handleApplyProgression}
                disabled={applyingProgression}
                title="Applique les decisions deja enregistrees : promotion, diplome, redoublement, archivage"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-all disabled:opacity-50"
              >
                {applyingProgression ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <GraduationCap className="w-4 h-4" />
                )}
                {applyingProgression ? 'Application...' : "Appliquer le passage d'annee"}
              </button>
            )}

            {/* Same, for every PV of the selected year */}
            {canDecide && selectedAcademicYearId && (
              <button
                onClick={handleApplyProgressionForYear}
                disabled={applyingProgression}
                title="Applique les decisions deja enregistrees sur tous les PV de l'annee selectionnee"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50"
              >
                {applyingProgression ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <GraduationCap className="w-4 h-4" />
                )}
                Toute l'annee
              </button>
            )}

            {/* Export dropdown */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExportDropdownOpen(!exportDropdownOpen);
                }}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-all"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Exporter
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {exportDropdownOpen && (
                <div
                  className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-30"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={handleExportPdf}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="w-4 h-4 text-red-500" />
                    Exporter PDF
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="w-4 h-4 text-green-500" />
                    Exporter Excel
                  </button>
                  {pvData.isFinalYear && (
                    <button
                      onClick={handleExportDiplomes}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                    >
                      <Award className="w-4 h-4 text-amber-500" />
                      Liste des Diplomes
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Print button */}
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
            >
              <Printer className="w-4 h-4" />
              Imprimer
            </button>
          </div>
        )}
      </div>

      {/* ==================== FILTER BAR ==================== */}
      <div className="no-print bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className={`grid grid-cols-2 gap-3 ${selectedFiliere && optionRequired ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          {/* Academic Year */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Annee Academique
            </label>
            <select
              value={selectedAcademicYearId}
              onChange={(e) => setSelectedAcademicYearId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">-- Selectionner --</option>
              {filtersData?.academicYears.map((ay) => (
                <option key={ay.id} value={ay.id}>
                  {formatAcademicYearName(ay.name)} {ay.isCurrent ? '(En cours)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Filiere */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Filiere
            </label>
            <select
              value={selectedFiliereId}
              onChange={(e) => {
                setSelectedFiliereId(e.target.value);
                setSelectedOptionId('');
                setSelectedNiveau('');
                setPvData(null);
              }}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">-- Selectionner --</option>
              {filtersData?.filieres.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.code} - {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Option — shown only at niveaux where the options have started;
              at those niveaux the cohort is split into options, so picking
              one is mandatory (no tronc commun anymore) */}
          {selectedFiliere && optionRequired && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Option
              </label>
              <select
                value={selectedOptionId}
                onChange={(e) => {
                  setSelectedOptionId(e.target.value);
                  setPvData(null);
                }}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">-- Selectionner l'option --</option>
                {visibleOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.optionName || o.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Niveau */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Niveau
            </label>
            <select
              value={selectedNiveau}
              onChange={(e) => setSelectedNiveau(e.target.value ? Number(e.target.value) : '')}
              disabled={!selectedFiliereId}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">-- Selectionner --</option>
              {availableNiveaux.map((n) => (
                <option key={n} value={n}>
                  Niveau {n}
                </option>
              ))}
            </select>
          </div>

          {/* Campus (required) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Campus
            </label>
            <select
              value={selectedCampusId}
              onChange={(e) => setSelectedCampusId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">-- Selectionner --</option>
              {filtersData?.campuses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ==================== CONTENT AREA ==================== */}

      {/* State A: No filters selected */}
      {!filtersComplete && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
          <div className="flex flex-col items-center text-center">
            <div className="p-4 bg-blue-100 rounded-full mb-4">
              <Scale className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              Jury de Deliberation
            </h3>
            <p className="text-sm text-blue-700 max-w-md">
              Selectionnez une annee academique, une filiere (et son option le cas echeant),
              un niveau et un campus pour afficher ou generer le PV de deliberation.
            </p>
          </div>
        </div>
      )}

      {/* State B: Loading PV */}
      {filtersComplete && pvLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      )}

      {/* Error loading PV */}
      {filtersComplete && pvError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{pvError}</p>
        </div>
      )}

      {/* State C: No PV exists */}
      {filtersComplete && !pvLoading && !pvError && !pvData && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <div className="flex flex-col items-center text-center">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun PV trouve
            </h3>
            <p className="text-sm text-gray-500 max-w-md mb-6">
              Aucun proces-verbal de deliberation n'existe pour cette combinaison.
              Generez-en un pour commencer.
            </p>

            {/* Generate error */}
            {generateError && (
              <div className="w-full max-w-lg mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 mb-1">
                      Impossible de generer le PV
                    </p>
                    <p className="text-sm text-red-700 whitespace-pre-line">
                      {generateError}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleGeneratePv}
              disabled={generating}
              className="group flex items-center gap-2.5 px-6 py-3 text-white font-medium rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {generating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Scale className="w-5 h-5" />
              )}
              {generating ? 'Generation en cours...' : 'Generer le PV'}
            </button>
          </div>
        </div>
      )}

      {/* State D: PV exists - main table */}
      {filtersComplete && !pvLoading && pvData && columnStructure && (
        <div className="space-y-4">
          {/* Read-only notice for roles that may consult but not decide */}
          {canManage && !canDecide && (
            <div className="no-print bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                Consultation seule. Les decisions du jury sont saisies par le Directeur Pedagogique
                ou le Directeur General.
              </p>
            </div>
          )}

          {/* Locked banner: signed by the Directeur Pedagogique */}
          {isPvLocked && (
            <div className="no-print bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <span className="font-semibold">PV verrouille.</span>{' '}
                {canUnlockPv
                  ? 'Il a ete signe par le Directeur Pedagogique. Vous pouvez encore le corriger : toute modification met a jour le passage d\'annee des etudiants concernes.'
                  : 'Il a ete signe par le Directeur Pedagogique et ne peut plus etre modifie. Seul le Directeur Pedagogique peut le corriger.'}
              </p>
            </div>
          )}

          {/* Signatures banner */}
          {((pvData.signatures?.length ?? 0) > 0 || pvData.signedByName) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-1">
                {(pvData.signatures?.length ?? 0) > 0 ? (
                  pvData.signatures.map((s) => (
                    <p key={s.userId} className="text-sm text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>
                        Signe electroniquement par{' '}
                        <span className="font-semibold">{s.name}</span>
                        , {mapRoleToFrench(s.role)}, le {formatDate(s.signedAt)}
                      </span>
                    </p>
                  ))
                ) : (
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>
                      Signe electroniquement par{' '}
                      <span className="font-semibold">{pvData.signedByName}</span>
                      {pvData.signedByRole && <>, {mapRoleToFrench(pvData.signedByRole)}</>}
                      {pvData.signedAt && <>, le {formatDate(pvData.signedAt)}</>}
                    </span>
                  </p>
                )}
              </div>
              {canManage && user?.id && !pvData.signatures?.some((s) => s.userId === user.id) && (
                <button
                  onClick={handleSignPv}
                  disabled={signing}
                  className="no-print flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-all disabled:opacity-50"
                >
                  {signing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Signer le PV
                </button>
              )}
            </div>
          )}

          {/* PV reference and info */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4 text-sm">
                {pvData.pvReference && (
                  <span className="text-gray-500">
                    Ref: <span className="font-mono font-semibold text-gray-900">{pvData.pvReference}</span>
                  </span>
                )}
                <span className="text-gray-400">|</span>
                <span className="text-gray-700 font-medium">
                  {pvData.filiereName} ({pvData.filiereCode})
                </span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">
                  {pvData.campusName}
                </span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">
                  Niveau {pvData.niveau} - {formatAcademicYearName(pvData.academicYearName)}
                </span>
                {pvData.isFinalYear && (
                  <>
                    <span className="text-gray-400">|</span>
                    <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5 text-xs font-medium">
                      <Award className="w-3.5 h-3.5" />
                      Annee Finale - {pvData.diplome}
                    </span>
                  </>
                )}
              </div>
              <div className="text-xs text-gray-400">
                {sortedStudents.length} etudiant{sortedStudents.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* ==================== THE PV TABLE ==================== */}
          <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm bg-white">
            <table className="min-w-full text-xs">
              {/* ---- TABLE HEADER (3 rows) ---- */}
              <thead>
                {/* Row 1: NOM/PRENOM + Semester spans + Annual avg + Decision headers */}
                <tr>
                  {/* NOM */}
                  <th
                    rowSpan={3}
                    className="sticky left-0 z-20 bg-gray-100 border-b border-r border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]"
                  >
                    Nom
                  </th>
                  {/* PRENOM */}
                  <th
                    rowSpan={3}
                    className="sticky left-[120px] z-20 bg-gray-100 border-b border-r border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]"
                  >
                    Prenom
                  </th>

                  {/* Semester headers */}
                  {columnStructure.semesterMeta.map((sem) => {
                    const colors = getSemesterColor(sem.number);
                    return (
                      <th
                        key={`sem-${sem.number}`}
                        colSpan={sem.totalColSpan}
                        className={`${colors.header} border-b border-r border-gray-300 px-2 py-2 text-center font-semibold uppercase tracking-wider`}
                      >
                        Resultats S{sem.number}
                      </th>
                    );
                  })}

                  {/* Annual average */}
                  <th
                    rowSpan={3}
                    className="bg-indigo-600 text-white border-b border-r border-gray-300 px-2 py-2 text-center font-semibold uppercase tracking-wider min-w-[60px]"
                  >
                    Moy. Annee
                  </th>

                  {/* Decision Pre-Jury */}
                  <th
                    rowSpan={3}
                    className="bg-gray-200 border-b border-r border-gray-300 px-2 py-2 text-center font-semibold text-gray-700 uppercase tracking-wider min-w-[130px]"
                  >
                    Dec. Pre-Jury
                  </th>

                  {/* Decision Officiel */}
                  <th
                    rowSpan={3}
                    className="bg-gray-200 border-b border-r border-gray-300 px-2 py-2 text-center font-semibold text-gray-700 uppercase tracking-wider min-w-[150px]"
                  >
                    Dec. Officiel
                  </th>

                  {/* Mention */}
                  <th
                    rowSpan={3}
                    className="bg-amber-100 border-b border-gray-300 px-2 py-2 text-center font-semibold text-amber-800 uppercase tracking-wider min-w-[120px]"
                  >
                    Mention
                  </th>
                </tr>

                {/* Row 2: Module names (within each semester) */}
                <tr>
                  {columnStructure.semesterMeta.map((sem) => {
                    const colors = getSemesterColor(sem.number);
                    return (
                      <React.Fragment key={`mod-row-sem-${sem.number}`}>
                        {sem.modules.map((mod) => (
                          <React.Fragment key={`mod-${mod.id}`}>
                            {/* Module name spanning element cols */}
                            <th
                              colSpan={mod.elements.length}
                              className={`${colors.moduleHeader} border-b border-r border-gray-200 px-1 py-1.5 text-center font-medium truncate max-w-[200px]`}
                              title={mod.name}
                            >
                              {mod.code}
                            </th>
                            {/* Module average header */}
                            <th
                              className={`${colors.avgBg} border-b border-r border-gray-300 px-1 py-1.5 text-center min-w-[45px]`}
                            >
                              Moy.
                            </th>
                          </React.Fragment>
                        ))}
                        {/* Semester average header */}
                        <th
                          className={`${colors.semAvgBg} border-b border-r border-gray-300 px-1 py-1.5 text-center min-w-[50px]`}
                        >
                          Moy. S{sem.number}
                        </th>
                      </React.Fragment>
                    );
                  })}
                </tr>

                {/* Row 3: Matiere (element) names */}
                <tr>
                  {columnStructure.semesterMeta.map((sem) => (
                    <React.Fragment key={`elem-row-sem-${sem.number}`}>
                      {sem.modules.map((mod) => (
                        <React.Fragment key={`elem-mod-${mod.id}`}>
                          {mod.elements.map((el) => (
                            <th
                              key={`el-${el.id}`}
                              className="bg-gray-50 border-b border-r border-gray-200 px-1 py-1.5 text-center font-normal text-gray-600 min-w-[50px] max-w-[80px] truncate"
                              title={el.name}
                            >
                              {el.name}
                            </th>
                          ))}
                          {/* Module avg placeholder (already covered by rowSpan, but we need a cell for the third row) */}
                          <th className="bg-gray-50 border-b border-r border-gray-300 px-1 py-1.5" />
                        </React.Fragment>
                      ))}
                      {/* Semester avg placeholder */}
                      <th className="bg-gray-50 border-b border-r border-gray-300 px-1 py-1.5" />
                    </React.Fragment>
                  ))}
                </tr>
              </thead>

              {/* ---- TABLE BODY ---- */}
              <tbody className="divide-y divide-gray-100">
                {sortedStudents.map((student, rowIdx) => {
                  const decisions = localDecisions.get(student.studentId);

                  return (
                    <tr
                      key={student.studentId}
                      className={`${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-indigo-50/30 transition-colors`}
                    >
                      {/* NOM (sticky) */}
                      <td
                        className={`sticky left-0 z-10 border-r border-gray-200 px-3 py-2 font-medium text-gray-900 truncate max-w-[120px] ${
                          rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                        title={student.studentName.split(' ').slice(-1)[0]}
                      >
                        {student.studentName.split(' ').slice(-1)[0]}
                      </td>

                      {/* PRENOM (sticky) */}
                      <td
                        className={`sticky left-[120px] z-10 border-r border-gray-200 px-3 py-2 text-gray-700 truncate max-w-[120px] ${
                          rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                        title={student.studentName.split(' ').slice(0, -1).join(' ')}
                      >
                        {student.studentName.split(' ').slice(0, -1).join(' ')}
                      </td>

                      {/* Grade cells by semester -> module -> element */}
                      {pvData.semesters.map((sem) => {
                        const colors = getSemesterColor(sem.number);
                        return (
                          <React.Fragment key={`body-sem-${sem.number}-${student.studentId}`}>
                            {sem.modules.map((mod) => (
                              <React.Fragment key={`body-mod-${mod.id}-${student.studentId}`}>
                                {/* Element grades */}
                                {mod.elements.map((el) => {
                                  const grade = student.grades[el.id] ?? null;
                                  return (
                                    <td
                                      key={`grade-${el.id}-${student.studentId}`}
                                      className={`border-r border-gray-100 px-1 py-1.5 text-center tabular-nums ${gradeColor(grade)}`}
                                    >
                                      {grade !== null ? grade.toFixed(2) : '-'}
                                    </td>
                                  );
                                })}

                                {/* Module average */}
                                <td
                                  className={`border-r border-gray-200 px-1 py-1.5 text-center tabular-nums ${colors.avgBg}`}
                                >
                                  {student.moduleAverages[mod.id] !== null && student.moduleAverages[mod.id] !== undefined
                                    ? student.moduleAverages[mod.id]!.toFixed(2)
                                    : '-'}
                                </td>
                              </React.Fragment>
                            ))}

                            {/* Semester average */}
                            <td
                              className={`border-r border-gray-200 px-1 py-1.5 text-center tabular-nums ${colors.semAvgBg}`}
                            >
                              {student.semesterAverages[String(sem.number)] !== null &&
                              student.semesterAverages[String(sem.number)] !== undefined
                                ? student.semesterAverages[String(sem.number)]!.toFixed(2)
                                : '-'}
                            </td>
                          </React.Fragment>
                        );
                      })}

                      {/* Annual average */}
                      <td className="border-r border-gray-200 px-1 py-1.5 text-center tabular-nums bg-indigo-600 text-white font-bold">
                        {student.annualAverage !== null
                          ? student.annualAverage.toFixed(2)
                          : '-'}
                      </td>

                      {/* Decision Pre-Jury */}
                      <td className="border-r border-gray-200 px-1 py-1.5">
                        <select
                          value={decisions?.decisionPreJury || ''}
                          onChange={(e) =>
                            handleDecisionChange(student.studentId, 'decisionPreJury', e.target.value)
                          }
                          disabled={!canEditDecisions}
                          className={`w-full text-xs border border-gray-200 rounded px-1 py-1 focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 ${canEditDecisions ? 'bg-white' : 'bg-gray-50 cursor-not-allowed'}`}
                        >
                          <option value="">--</option>
                          {PRE_JURY_DECISIONS.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Decision Officiel */}
                      <td className="border-r border-gray-200 px-1 py-1.5">
                        <select
                          value={decisions?.decisionOfficiel || ''}
                          onChange={(e) =>
                            handleDecisionChange(student.studentId, 'decisionOfficiel', e.target.value)
                          }
                          disabled={!canEditDecisions}
                          className={`w-full text-xs border border-gray-200 rounded px-1 py-1 focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 ${canEditDecisions ? 'bg-white' : 'bg-gray-50 cursor-not-allowed'}`}
                        >
                          <option value="">--</option>
                          {OFFICIEL_DECISIONS.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Mention */}
                      <td className="px-1 py-1.5">
                        <select
                          value={decisions?.mention || ''}
                          onChange={(e) =>
                            handleDecisionChange(student.studentId, 'mention', e.target.value)
                          }
                          disabled={!canEditDecisions}
                          className={`w-full text-xs border border-gray-200 rounded px-1 py-1 focus:ring-1 focus:ring-amber-400 focus:border-amber-400 ${canEditDecisions ? 'bg-white' : 'bg-gray-50 cursor-not-allowed'}`}
                        >
                          <option value="">--</option>
                          {MENTIONS.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Row count footer */}
          <div className="no-print text-xs text-gray-400 text-right">
            {sortedStudents.length} etudiant{sortedStudents.length !== 1 ? 's' : ''} dans ce PV
          </div>

          {/* ==================== PRINT-ONLY FOOTER ==================== */}
          <div className="hidden print:block mt-8 border-t border-gray-300 pt-6">
            <div className="flex justify-between items-start mb-6">
              <div className="text-xs text-gray-500">
                {pvData.pvReference && <p>Ref: {pvData.pvReference}</p>}
                <p>
                  {pvData.filiereName} - Niveau {pvData.niveau} - {formatAcademicYearName(pvData.academicYearName)}
                </p>
              </div>
              <div className="text-xs text-gray-500 text-right">
                <p>
                  Genere le{' '}
                  {new Intl.DateTimeFormat('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  }).format(new Date())}
                </p>
              </div>
            </div>

            <p className="text-sm font-bold mb-4">Membres du Jury :</p>
            <div className="mt-4 space-y-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-end gap-4">
                  <span>Nom: ________________________________</span>
                  <span>Signature: ________________________________</span>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center text-xs text-gray-400">
              <p>Document genere automatiquement par NEXEDU - Systeme de gestion academique</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JuryDeliberationPage;
